import { Fragment, useState } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';

export const InputSelectAccountBank = ({
	name,
	label,
	onChange,
	value,
	placeholder,
	disabled,
	options = [],
	required = false,
	error = null,
}) => {
	const [touched, setTouched] = useState(false);
	const safeOptions = Array.isArray(options) ? options : [];

	const selectedAccount = safeOptions.find(
		(account) => account.value === value || account._id === value
	);

	const hasValue = Boolean(value && String(value).trim() !== '');
	const internalError =
		required && touched && !hasValue && !error ? 'Este campo es requerido' : null;
	const displayError = error || internalError;

	const handleChange = (selectedValue) => {
		const syntheticEvent = {
			target: {
				name: name,
				value: selectedValue,
			},
		};
		onChange(syntheticEvent);
	};

	const buttonRingClasses = displayError
		? 'ring-2 ring-inset ring-red-500 bg-red-50 focus:ring-2 focus:ring-inset focus:ring-red-500'
		: 'ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600';

	return (
		<div className="w-full">
			<Listbox value={value} onChange={handleChange} disabled={disabled}>
				{({ open }) => (
					<>
						<Listbox.Label className="block text-md font-semibold text-gray-900 leading-6 ml-1">
							{label}
							{required && (
								<span className="text-red-500 ml-1" aria-hidden="true">
									*
								</span>
							)}
						</Listbox.Label>
						<div className="relative mt-3">
							<Listbox.Button
								type="button"
								onBlur={() => setTouched(true)}
								aria-required={required}
								aria-invalid={displayError ? true : undefined}
								className={`relative w-full cursor-default rounded-md bg-white py-2 pl-3 pr-10 text-left text-gray-900 shadow-sm focus:outline-none sm:text-sm sm:leading-6 disabled:opacity-50 disabled:cursor-not-allowed ${buttonRingClasses}`}
							>
								<span className="flex items-center uppercase">
									{selectedAccount ? (
										<>
											<img
												src={selectedAccount.urlName}
												alt={selectedAccount.name}
												className="h-6 w-6 flex-shrink-0 rounded-full object-cover"
											/>
											<span
												className={`ml-2 block truncate ${displayError ? 'text-red-900' : 'text-gray-500'}`}
											>
												{selectedAccount.name}
											</span>
										</>
									) : (
										<span
											className={`block truncate ${displayError ? 'text-red-400' : 'text-gray-400'}`}
										>
											{placeholder}
										</span>
									)}
								</span>
								<span className="pointer-events-none absolute inset-y-0 right-0 ml-3 flex items-center pr-2">
									<ChevronUpDownIcon
										className={`h-5 w-5 ${displayError ? 'text-red-400' : 'text-gray-400'}`}
										aria-hidden="true"
									/>
								</span>
							</Listbox.Button>

							<Transition
								show={open}
								as={Fragment}
								leave="transition ease-in duration-100"
								leaveFrom="opacity-100"
								leaveTo="opacity-0"
							>
								<Listbox.Options className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
									{safeOptions.map((account) => (
										<Listbox.Option
											key={account.value || account._id}
											className={({ active }) =>
												`relative cursor-default select-none py-2 pl-3 pr-9 ${
													active ? 'bg-indigo-600 text-white' : 'text-gray-900'
												}`
											}
											value={account.value || account._id}
										>
											{({ selected, active }) => (
												<>
													<div className="flex items-center">
														<img
															src={account.urlName}
															alt={account.name}
															className="h-6 w-6 flex-shrink-0 rounded-md object-cover"
														/>
														<span
															className={`ml-2 block truncate ${active ? 'text-indigo-200' : 'text-gray-500'}`}
														>
															{account.name}
														</span>
													</div>

													{selected ? (
														<span
															className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
																active ? 'text-white' : 'text-indigo-600'
															}`}
														>
															<CheckIcon className="h-5 w-5" aria-hidden="true" />
														</span>
													) : null}
												</>
											)}
										</Listbox.Option>
									))}
								</Listbox.Options>
							</Transition>
						</div>
					</>
				)}
			</Listbox>
			{displayError && (
				<p className="mt-2 text-sm text-red-600 flex items-center gap-1 ml-1">
					<svg className="w-4 h-4 shrink-0" fill="currentColor" viewBox="0 0 20 20">
						<path
							fillRule="evenodd"
							d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
							clipRule="evenodd"
						/>
					</svg>
					{displayError}
				</p>
			)}
		</div>
	);
};
